import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FiCreditCard,
  FiHexagon,
  FiDollarSign,
  FiClock,
  FiCheckCircle,
  FiSlash,
  FiRefreshCw,
} from 'react-icons/fi';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import DashboardShell from '../../components/DashboardShell/DashboardShell';
import PageHeader from '../../components/PageHeader/PageHeader';
import WalletBadge from '../../components/WalletBadge/WalletBadge';
import WithdrawalModal from '../../components/WithdrawalModal/WithdrawalModal';
import KycBanner from '../../components/KycBanner/KycBanner';
import WalletHistory from '../../components/WalletHistory/WalletHistory';
import { useCurrency } from '../../app/CurrencyContext';
import styles from '../InvestmentDashboard/InvestmentDashboard.module.css';
import axiosClient from '../../api/axiosClient';

type RequestStatus = 'pending' | 'processing' | 'paid' | 'rejected';

interface WithdrawalRequest {
  id: string;
  type: string;
  amount: string;
  amount_inr_equivalent: string | null;
  currency: string;
  status: RequestStatus;
  payment_method_type: string | null;
  wallet_address: string | null;
  account_holder_name: string | null;
  upi_id: string | null;
  account_number: string | null;
  ifsc_code: string | null;
  note: string | null;
  package_name: string | null;
  created_at: string;
  admin_charge_pct: string | null;
  admin_charge_amount: string | null;
  net_payable_amount: string | null;
  payout_reference: string | null;
  payout_paid_at: string | null;
  payout_reviewed_at: string | null;
  payout_failure_reason: string | null;
}

const STATUS_COLORS: Record<RequestStatus, string> = {
  pending: '#e8a86a',
  processing: '#1d60f6',
  paid: '#5cb87d',
  rejected: '#e2534a',
};

const STATUS_LABELS: Record<RequestStatus, string> = {
  pending: 'Pending',
  processing: 'Processing',
  paid: 'Paid',
  rejected: 'Rejected',
};

function money(
  value: string | number | null | undefined,
  currency: string
) {
  if (value === null || value === undefined || value === '') {
    return 'Not recorded';
  }

  const amount = Number(value);

  if (!Number.isFinite(amount)) return 'Not recorded';

  const formatted = amount.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return currency === 'INR'
    ? `₹${formatted}`
    : `${currency} ${formatted}`;
}

function dateLabel(value: string | null) {
  if (!value) return 'Not recorded';

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? 'Not recorded'
    : date.toLocaleString('en-IN');
}

const Funds: React.FC = () => {
  const { convertTransaction, format } = useCurrency();

  const [modal, setModal] = useState<'fiat' | 'crypto' | null>(null);
  const [requests, setRequests] = useState<WithdrawalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [filter, setFilter] = useState('all');
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<string | null>(null);

  const cancellingRef = useRef(false);

  const refresh = () => setRefreshKey((value) => value + 1);

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setLoadError('');

      try {
        const { data } = await axiosClient.get('/funds/mine');

        if (!active) return;

        setRequests(
          (data.requests || []).filter(
            (request: WithdrawalRequest) => request.type === 'withdrawal'
          )
        );
      } catch (error: any) {
        if (!active) return;

        setLoadError(
          error.response?.data?.message
          || 'Could not load withdrawal history.'
        );
      } finally {
        if (active) setLoading(false);
      }
    };

    void load();

    return () => {
      active = false;
    };
  }, [refreshKey]);

  const handleCancel = async (id: string) => {
    if (cancellingRef.current) return;

    cancellingRef.current = true;
    setCancellingId(id);

    try {
      await axiosClient.patch(`/funds/${id}/cancel`);

      toast.success(
        'Withdrawal cancelled. The reserved amount is available again.'
      );

      setCancelTarget(null);
      window.dispatchEvent(new Event('wallet:refresh'));
      refresh();
    } catch (error: any) {
      toast.error(
        error.response?.data?.message
        || 'Could not cancel this withdrawal.'
      );
    } finally {
      cancellingRef.current = false;
      setCancellingId(null);
    }
  };

  const paidRequests = useMemo(
    () => requests.filter((request) => request.status === 'paid'),
    [requests]
  );

  const totalGrossWithdrawn = paidRequests.reduce(
    (total, request) => total + convertTransaction(
      Number(request.amount),
      request.currency || 'INR',
      Number(request.amount_inr_equivalent ?? request.amount)
    ),
    0
  );

  const pendingCount = requests.filter(
    (request) => request.status === 'pending'
  ).length;

  const processingCount = requests.filter(
    (request) => request.status === 'processing'
  ).length;

  const visibleRequests = requests.filter(
    (request) => filter === 'all' || request.status === filter
  );

  const barData = useMemo(() => {
    const grouped: Record<
      string,
      { date: string; amount: number }
    > = {};

    paidRequests.forEach((request) => {
      const date = new Date(
        request.payout_paid_at || request.created_at
      );

      if (Number.isNaN(date.getTime())) return;

      const key = [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, '0'),
        String(date.getDate()).padStart(2, '0'),
      ].join('-');

      if (!grouped[key]) {
        grouped[key] = {
          date: date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          }),
          amount: 0,
        };
      }

      grouped[key].amount += convertTransaction(
        Number(request.amount),
        request.currency || 'INR',
        Number(request.amount_inr_equivalent ?? request.amount)
      );
    });

    return Object.keys(grouped)
      .sort()
      .map((key) => grouped[key]);
  }, [paidRequests, convertTransaction]);

  const summaryReady = !loading && !loadError;

  return (
    <DashboardShell>
      <PageHeader
        title="Withdrawals"
        subtitle="Request a payout and track charges, payment details and status."
        action={<WalletBadge />}
      />

      <div className={styles.page}>
        <KycBanner />

        <Link
          to="/dashboard/investment"
          style={{
            display: 'inline-block',
            marginBottom: 20,
            color: 'var(--copper-light)',
          }}
        >
          Go to Deposit / Investment →
        </Link>

        <div className={styles.statsGrid}>
          <div className={styles.statPill}>
            <span>Completed Withdrawals — Gross</span>
            <b>
              {summaryReady ? format(totalGrossWithdrawn) : '—'}
            </b>
            <small>Includes charges where recorded</small>
            <FiDollarSign className={styles.statIcon} />
          </div>

          <div className={`${styles.statPill} ${styles.borderWarn}`}>
            <span>Pending Requests</span>
            <b className={styles.warnText}>
              {summaryReady ? pendingCount : '—'}
            </b>
            <FiClock className={styles.statIcon} />
          </div>

          <div className={styles.statPill}>
            <span>Processing</span>
            <b>{summaryReady ? processingCount : '—'}</b>
            <FiClock className={styles.statIcon} />
          </div>

          <div className={`${styles.statPill} ${styles.borderSuccess}`}>
            <span>Paid Requests</span>
            <b className={styles.successText}>
              {summaryReady ? paidRequests.length : '—'}
            </b>
            <FiCheckCircle className={styles.statIcon} />
          </div>
        </div>

        <div className={styles.mainGrid}>
          <div className={styles.optionCol}>
            <button
              type="button"
              className={styles.optionCardSmall}
              onClick={() => setModal('fiat')}
            >
              <div
                className={`${styles.optionIcon} ${styles.optionIconGreen}`}
              >
                <FiCreditCard />
              </div>
              <div>
                <b>Cash Withdrawal</b>
                <span>Bank transfer or UPI</span>
              </div>
            </button>

            <button
              type="button"
              className={styles.optionCardSmall}
              onClick={() => setModal('crypto')}
            >
              <div
                className={`${styles.optionIcon} ${styles.optionIconBlue}`}
              >
                <FiHexagon />
              </div>
              <div>
                <b>USDT to INR</b>
                <span>INR payout to bank or UPI</span>
              </div>
            </button>
          </div>

          <div className={styles.card}>
            <h3>Completed Withdrawals — Gross Amount</h3>

            {loading ? (
              <div className={styles.chartEmpty}>Loading…</div>
            ) : loadError ? (
              <div className={styles.chartEmpty}>
                Withdrawal data is unavailable.
              </div>
            ) : barData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart
                    data={barData}
                    margin={{ top: 15, right: 15, left: 0, bottom: 10 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="rgba(128,128,128,.2)"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10, fill: 'var(--muted)' }}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: 'var(--muted)' }}
                      tickLine={false}
                    />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null;

                        return (
                          <div className={styles.tooltip}>
                            <b>{label}</b>
                            <span>
                              Gross: {format(Number(payload[0].value))}
                            </span>
                          </div>
                        );
                      }}
                    />
                    <Bar
                      dataKey="amount"
                      fill="#1d60f6"
                      radius={[6, 6, 0, 0]}
                      maxBarSize={60}
                    />
                  </BarChart>
                </ResponsiveContainer>

                <p className={styles.rowSub}>
                  Older records without a paid date use their request date.
                </p>
              </>
            ) : (
              <div className={styles.chartEmpty}>
                No completed withdrawals yet.
              </div>
            )}
          </div>
        </div>

        <div className={styles.card}>
          <div className={styles.historyHeader}>
            <h3>Withdrawal History</h3>

            <button
              type="button"
              onClick={refresh}
              disabled={loading || !!cancellingId}
              style={{
                background: 'transparent',
                color: 'inherit',
                border: '1px solid var(--muted)',
                padding: '8px 12px',
                borderRadius: 8,
                cursor: 'pointer',
              }}
            >
              <FiRefreshCw /> Refresh
            </button>
          </div>

          <label
            htmlFor="withdrawal-history-filter"
            style={{ display: 'block', margin: '14px 0' }}
          >
            Status
            <select
              id="withdrawal-history-filter"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              style={{
                marginLeft: 12,
                padding: '8px 12px',
                background: 'var(--panel)',
                color: 'inherit',
                border: '1px solid var(--muted)',
                borderRadius: 8,
              }}
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="paid">Paid</option>
              <option value="rejected">Rejected</option>
            </select>
          </label>

          {loading && (
            <div className={styles.chartEmpty}>Loading history…</div>
          )}

          {!loading && loadError && (
            <div className={styles.chartEmpty} role="alert">
              {loadError}
            </div>
          )}

          {!loading && !loadError && visibleRequests.length === 0 && (
            <div className={styles.chartEmpty}>
              No withdrawal requests match this filter.
            </div>
          )}

          {!loading && !loadError && (
            <div className={styles.list}>
              {visibleRequests.map((request) => {
                const currency = request.currency || 'INR';
                const rejected = request.status === 'rejected';

                return (
                  <div
                    key={request.id}
                    className={styles.row}
                    style={{
                      display: 'block',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 12,
                        flexWrap: 'wrap',
                      }}
                    >
                      <b>{money(request.amount, currency)}</b>

                      <span
                        className={styles.statusPill}
                        style={{
                          color: STATUS_COLORS[request.status],
                          borderColor: STATUS_COLORS[request.status],
                        }}
                      >
                        {STATUS_LABELS[request.status] || request.status}
                      </span>
                    </div>

                    <div className={styles.rowSub}>
                      {request.payment_method_type || 'Withdrawal'}
                      {' · '}
                      {dateLabel(request.created_at)}
                    </div>

                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns:
                          'repeat(auto-fit, minmax(160px, 1fr))',
                        gap: 14,
                        margin: '18px 0',
                      }}
                    >
                      <div>
                        <div className={styles.rowSub}>Requested</div>
                        <b>{money(request.amount, currency)}</b>
                      </div>

                      <div>
                        <div className={styles.rowSub}>
                          Admin Charge
                          {request.admin_charge_pct != null
                            ? ` (${Number(request.admin_charge_pct)}%)`
                            : ''}
                        </div>

                        <b>
                          {rejected
                            ? `${money(0, currency)} collected`
                            : money(request.admin_charge_amount, currency)}
                        </b>

                        {!rejected && request.status !== 'paid' && (
                          <div className={styles.rowSub}>
                            Deducted on successful payout
                          </div>
                        )}
                      </div>

                      <div>
                        <div className={styles.rowSub}>
                          {request.status === 'paid'
                            ? 'Net Paid'
                            : rejected
                              ? 'Payment'
                              : 'Net Payable'}
                        </div>

                        <b>
                          {rejected
                            ? 'Not paid'
                            : money(request.net_payable_amount, currency)}
                        </b>
                      </div>
                    </div>

                    {request.account_holder_name && (
                      <div className={styles.rowSub}>
                        Account holder: {request.account_holder_name}
                      </div>
                    )}

                    {request.upi_id && (
                      <div className={styles.rowSub}>
                        UPI: {request.upi_id}
                      </div>
                    )}

                    {request.account_number && (
                      <div className={styles.rowSub}>
                        Account: {request.account_number}
                        {' · '}
                        IFSC: {request.ifsc_code || '—'}
                      </div>
                    )}

                    {request.wallet_address && (
                      <div className={styles.rowSub}>
                        Recorded wallet: {request.wallet_address}
                      </div>
                    )}

                    {request.payout_reference && (
                      <div className={styles.rowSub}>
                        Payment reference: {request.payout_reference}
                      </div>
                    )}

                    {request.payout_paid_at && (
                      <div className={styles.rowSub}>
                        Paid at: {dateLabel(request.payout_paid_at)}
                      </div>
                    )}

                    {request.payout_reviewed_at && (
                      <div className={styles.rowSub}>
                        Reviewed at: {dateLabel(request.payout_reviewed_at)}
                      </div>
                    )}

                    {request.payout_failure_reason && (
                      <div
                        className={styles.rowSub}
                        style={{ color: '#e2534a', marginTop: 8 }}
                      >
                        Reason: {request.payout_failure_reason}
                      </div>
                    )}

                    {request.package_name && (
                      <div className={styles.rowSub}>
                        Purpose: {request.package_name}
                      </div>
                    )}

                    {request.note && (
                      <div className={styles.rowSub}>
                        Note: {request.note}
                      </div>
                    )}

                    <div className={styles.rowSub}>
                      Request ID: {request.id}
                    </div>

                    {request.status === 'pending' && (
                      <div style={{ marginTop: 14 }}>
                        {cancelTarget === request.id ? (
                          <>
                            <p className={styles.rowSub}>
                              Cancel this request and release the reserved amount?
                            </p>

                            <button
                              type="button"
                              disabled={!!cancellingId}
                              onClick={() => handleCancel(request.id)}
                              style={{
                                padding: '8px 12px',
                                borderRadius: 8,
                                border: '1px solid #e2534a',
                                background: 'transparent',
                                color: '#e2534a',
                                marginRight: 10,
                                cursor: 'pointer',
                              }}
                            >
                              {cancellingId === request.id
                                ? 'Cancelling…'
                                : 'Confirm Cancel'}
                            </button>

                            <button
                              type="button"
                              disabled={!!cancellingId}
                              onClick={() => setCancelTarget(null)}
                              style={{
                                padding: '8px 12px',
                                borderRadius: 8,
                                border: '1px solid var(--muted)',
                                background: 'transparent',
                                color: 'inherit',
                                cursor: 'pointer',
                              }}
                            >
                              Keep Request
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            disabled={!!cancellingId}
                            onClick={() => setCancelTarget(request.id)}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              padding: '8px 12px',
                              borderRadius: 8,
                              border: '1px solid #e2534a',
                              background: 'transparent',
                              color: '#e2534a',
                              cursor: 'pointer',
                            }}
                          >
                            <FiSlash /> Cancel Request
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <WalletHistory />

      {modal && (
        <WithdrawalModal
          method={modal}
          onClose={() => setModal(null)}
          onSuccess={refresh}
        />
      )}
    </DashboardShell>
  );
};

export default Funds;